import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot } from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px - env(safe-area-inset-top, 0px));
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  padding-bottom: max(2rem, calc(1rem + env(safe-area-inset-bottom, 0px)));

  ${iosNativePageRoot}
`;

const AuthContainer = styled.div`
  width: 100%;
  max-width: 420px;
  animation: slideUp 0.5s ease-out;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    font-family: ${theme.typography.fontFamily.secondary};
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary[600]};
    margin: 0;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    margin-top: 0.5rem;
  }
`;

const SocialButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const SocialButton = styled.button<{ $provider: 'google' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${props => {
    switch (props.$provider) {
      case 'google':
        return `
          background: white;
          color: #1f1f1f;
          border-color: #dadce0;
          
          &:hover {
            background: #f8f9fa;
            box-shadow: ${theme.shadows.sm};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg, img {
    width: 20px;
    height: 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean; $hasTrailingIcon?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 0.875rem 1rem;
  /* Reserve space for inline icon so long text (email/password) doesn't overlap - same idea as GlowBot */
  padding-right: ${props => props.$hasTrailingIcon ? '3rem' : '1rem'};
  border: 2px solid ${props => props.$hasError ? theme.colors.status.error : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}30;
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border.light};
  }
  
  span {
    color: ${theme.colors.text.tertiary};
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  
  p {
    color: ${theme.colors.text.secondary};
    margin: 0;
    font-size: ${theme.typography.fontSize.sm};
  }
  
  a {
    color: ${theme.colors.primary[600]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Terms = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  text-align: center;
  margin: 1rem 0 0;
  line-height: 1.5;
  
  a {
    color: ${theme.colors.primary[600]};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SignInPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Sign in error:', error);

      const errorCode = error.code || error.name || '';
      const errorMessage = error.message || '';

      if (errorCode === 'UserNotConfirmedException' || errorMessage.includes('not confirmed')) {
        localStorage.setItem('pendingVerificationEmail', email);
        navigate('/verify-email', { state: { email } });
        return;
      } else if (errorCode === 'auth/user-not-found' || errorMessage.includes('user-not-found')) {
        setError('No account found with this email address.');
      } else if (errorCode === 'auth/wrong-password' || errorMessage.includes('wrong-password')) {
        setError('Incorrect password. Please try again.');
      } else if (errorCode === 'auth/invalid-email' || errorMessage.includes('invalid-email')) {
        setError('Please enter a valid email address.');
      } else if (errorCode === 'auth/user-disabled') {
        setError('This account has been disabled.');
      } else if (errorCode === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (errorMessage.includes('network')) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to sign in. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: 'google') => {
    setError('');
    setSocialLoading(provider);
    
    try {
      switch (provider) {
        case 'google':
          await signInWithGoogle();
          break;
      }
      navigate(from, { replace: true });
    } catch (error: any) {
      // Error handling is done in AuthContext with toast notifications
      console.error(`${provider} sign-in error:`, error);
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <PageContainer>
      <AuthContainer>
        <Logo>
          <h1>GlowLens™</h1>
          <p>Welcome back! Let's continue your glow journey ✨</p>
        </Logo>

        <Card padding="lg">
          <SocialButtons>
            <SocialButton
              $provider="google"
              onClick={() => handleSocialSignIn('google')}
              disabled={!!socialLoading}
            >
              {socialLoading === 'google' ? (
                <LoadingSpinner size={20} />
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Continue with Google
            </SocialButton>

          </SocialButtons>

          <Terms>
            By continuing, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
          </Terms>

          <Divider>
            <span>or</span>
          </Divider>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="email">Email Address</Label>
              <InputContainer>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  $hasError={!!error}
                  $hasTrailingIcon
                />
                <Mail size={20} style={{ 
                  position: 'absolute', 
                  right: '1rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: theme.colors.text.tertiary,
                  pointerEvents: 'none'
                }} />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <InputContainer>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  $hasError={!!error}
                  $hasTrailingIcon
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </InputContainer>
            </InputGroup>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <Button
              type="submit"
              fullWidth
              size="lg"
              disabled={loading || !email || !password || !!socialLoading}
            >
              {loading ? (
                <>
                  <LoadingSpinner size={20} />
                  Signing In...
                </>
              ) : (
                'Sign In with Email'
              )}
            </Button>
          </Form>

          <LinkText>
            <p>Don't have an account? <Link to="/signup">Sign up for free</Link></p>
          </LinkText>
        </Card>
      </AuthContainer>
    </PageContainer>
  );
};

export default SignInPage;
